import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import Grid from "./src/Grid.js";
import MyQueryBuilder from "../QueryBuilder";
import ExportCSV from "./src/exportCSV";
import RankApplicationComponent from "./src/RankApplicationComponent";
import { Accordion, Button, Card } from "react-bootstrap";
import "./style.css";
import OperateOnSelectedRows from "./src/OperateOnSelectedRows";
import { useSelector } from "react-redux";

function ApplicationsTable(props) {
  const { year, term } = props;
  const history = useHistory();
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const logger = useSelector((state) => state.logger);

  return (
    <div>
      <div className="container section">
        <div className="text-right">
          <Button
            variant="primary"
            size="sm"
            onClick={() => history.push("/dashboard")}
          >
            Return to dashboard
          </Button>
        </div>
        <h3>Applications</h3>
        <Accordion defaultActiveKey="-1">
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="0">
                Quick Guide
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="0">
              <Card.Body>
                <ul>
                  <li>
                    Applicants choose up to four courses to apply for in each
                    hiring season.
                  </li>
                  <li>
                    These choices are organized in corresponding columns under
                    "First-" to "Fourth Choice" groups.
                  </li>
                  <li>
                    The corresponding columns have ordinal numbers appended to
                    their names.
                  </li>
                  <ul>
                    <li>
                      For example, <code>Course (1st)</code> is the applicant's
                      first choice. As another example, <code>Grade (3rd)</code>{" "}
                      is the applicant's grade for the course which they
                      selected as their third choice.
                    </li>
                  </ul>
                  <li>
                    <mark>
                      Faculty are expected to update <code>Feedback</code>{" "}
                      column, for each application, for the choice matches the
                      course they teach.
                    </mark>
                  </li>
                  <li>
                    <mark>
                      Faculty can also add notes regarding their feedback in the{" "}
                      <code>Note</code> column next to <code>Feedback</code>{" "}
                      column.
                    </mark>
                  </li>
                </ul>
                <details>
                  <summary>Navigation & Editing</summary>
                  <ul>
                    <li>Scroll from left to right to see all columns.</li>
                    <li>
                      Scroll from top to bottom to see all rows. (Infinite
                      scrolling is enabled.)
                    </li>
                    <li>
                      In the <u>Group</u> menu, you can group/ungroup columns
                      and expand/contract column groups.
                    </li>
                    <li>
                      <b>To edit a cell, double click on it.</b> If no editor
                      shows up, the cell is not editable.
                    </li>
                    <ul>
                      <li>
                        Alternatively, select a cell with one click and then hit{" "}
                        <code>Enter</code> (PC) / <code>Return</code> (MAC) or{" "}
                        <code>F2</code> to enable editing.
                      </li>
                      <li>
                        The default editor will clear the contents of the cell
                        if <code>Backspace</code> (PC) / <code>Delete</code>{" "}
                        (MAC) are pressed.
                      </li>
                    </ul>
                    <li>
                      <b>
                        To save changes, you can press the <code>Enter</code>{" "}
                        (PC) / <code>Return</code> (MAC) or click outside the
                        cell being edited.
                      </b>
                      <ul>
                        <li>
                          To save changes in multiline comment/note editor, you
                          must press the <code>Enter</code> (PC) /{" "}
                          <code>Return</code> (MAC) key.
                        </li>
                        <li>
                          To add multiline comment/note, hold <code>Shift</code>{" "}
                          and press <code>Enter</code> to go to a new line
                          (avoids closing the editor).
                        </li>
                      </ul>
                    </li>
                    <li>
                      <b>
                        To cancel/undo changes, press the <code>Esc</code> key.
                      </b>{" "}
                      The following keyboard shortcuts are available as well:
                    </li>
                    <ul>
                      <li>
                        <code>Ctrl+Z</code> (PC) / <code>CMD+Z</code> (Mac):
                        will undo the last cell edit(s).
                      </li>
                      <li>
                        <code>Ctrl+Y</code> (PC) / <code>CMD+SHIFT+Z</code>{" "}
                        (Mac): will redo the last undo.
                      </li>
                    </ul>
                  </ul>
                </details>
                <details>
                  <summary>Resizing & Rearranging</summary>
                  <ul>
                    <li>
                      All columns can be resized by dragging the resize handle
                      (right border of the column).
                    </li>
                    <li>
                      If you hold <code>Shift</code> while dragging the resize
                      handle, a column will take space away from the column
                      adjacent to it.
                    </li>
                    <li>
                      If you double click on the resize handle, the column will
                      adjust to fit the content.
                    </li>
                    <li>
                      In the <u>Size</u> menu, you can adjust column width
                      and/or table height to desired value.
                    </li>
                    <li>
                      To move (rearrange) columns, try dragging the column
                      header with the mouse.
                    </li>
                    <li>
                      To move (rearrange) rows, try dragging the <u>grip</u>{" "}
                      icon (left of row number) with the mouse.
                    </li>
                  </ul>
                </details>
                <details>
                  <summary>Selecting & Pinning</summary>
                  <ul>
                    <li>
                      Select a row by clicking on the checkbox next to its row
                      number.
                    </li>
                    <ul>
                      <li>
                        Selecting multiple rows can be achieved by holding down{" "}
                        <code>Ctrl</code> and mouse clicking the rows. A range
                        of rows can be selected by using <code>Shift</code>{" "}
                        instead.
                      </li>
                    </ul>
                    <li>
                      It is possible to pin one or more rows in the following
                      way:{" "}
                    </li>
                    <ol>
                      <li>Select the rows.</li>
                      <li>
                        In the <u>Pinning</u> menu, you can pin/unpin row to
                        top/bottom of the grid.
                      </li>
                    </ol>
                    <li>
                      It is possible to pin a column by moving the column in the
                      following ways:
                    </li>
                    <ol>
                      <li>
                        When other columns are pinned, drag the column to the
                        existing pinned area.
                      </li>
                      <li>
                        When no columns are pinned, drag the column to the edge
                        of the grid and wait for approximately one second. The
                        grid will then assume you want to pin and create a
                        pinned area and place the column into it.
                      </li>
                    </ol>
                  </ul>
                </details>
                <details>
                  <summary>Searching & Sorting</summary>
                  <ul>
                    <li>
                      You can sort a column by clicking on the column header.
                    </li>
                    <ul>
                      <li>Click once to sort in ascending order.</li>
                      <li>Click again to sort in descending order.</li>
                      <li>Click once again to "unsort" (undo sorting).</li>
                    </ul>
                    <li>
                      To sort by multiple columns, hold down <code>Shift</code>{" "}
                      while clicking the column header.
                    </li>
                    <li>
                      For quick search, use the "Quick filter" input. The quick
                      filter text will check all words provided against the full
                      row.
                    </li>
                    <li>
                      There is a filter widget for each column; hover over the
                      column name and click on the hamburger menu.
                    </li>
                    <ul>
                      <li>
                        You can apply multiple filters simultaneously; filters
                        are combined using logical <code>AND</code> operator.
                      </li>
                    </ul>
                    <li>
                      <mark>
                        For more advanced filtering, use the "Query Builder"
                        tool.
                      </mark>
                    </li>
                  </ul>
                </details>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="1">
                Export Data
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="1">
              <Card.Body>
                <ExportCSV year={year} term={term} />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="2">
                Query Builder
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="2">
              <Card.Body>
                <p>
                  You can use the query builder to create a complex filter to
                  temporarily hide some of the data in the table, so you can
                  focus on the data you want to see.
                </p>
                <p>
                  <span>
                    Make sure to click on the <u>Apply</u> button.
                  </span>{" "}
                  Please note the query is applied to the entirety of
                  applications ({term} {year}), not only the filtered view shown
                  in the table below.
                </p>
                <MyQueryBuilder year={year} term={term} />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="3">
                Rank Applicants
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="3">
              <Card.Body>
                <p>
                  <span>
                    This is an <u>experimental</u> feature.
                  </span>{" "}
                  The applications are ranked based on interest, experience,
                  grade and GPA. The ranking is applied to the entirety of
                  applications ({term} {year}), not only the filtered view shown
                  in the table below.
                </p>
                <RankApplicationComponent
                  year={year}
                  term={term}
                  gridApi={gridApi}
                  gridColumnApi={gridColumnApi}
                />
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <Card.Header>
              <Accordion.Toggle as={Button} variant="link" eventKey="4">
                Bulk Process
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="4">
              <Card.Body>
                <OperateOnSelectedRows gridApi={gridApi} />
                <br />
                <div>
                  <span>More operation to be added!</span>
                </div>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </div>
      <div className="container p-3 mb-2 bg-light text-muted">
        Action Logger: {logger.text}
      </div>
      <div className="container-fluid">
        <Grid
          year={year}
          term={term}
          gridApi={gridApi}
          setGridApi={setGridApi}
          gridColumnApi={gridColumnApi}
          setGridColumnApi={setGridColumnApi}
        />
      </div>
      <div
        className="section" /* keep this or the page footer will run into the bottom of the table! */
      />
    </div>
  );
}

export default ApplicationsTable;
